import {
  AlertTriangle,
  Briefcase,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  Fuel,
  Navigation,
  PanelRightClose,
  Route as RouteIcon,
  Sparkles,
  TrendingUp,
  Truck,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { checkOperatorSkillEligibility } from '../../lib/job-plan-eligibility';
import { recomputeStopSequence, type SequenceFeasibility } from '../../lib/job-plan-sequence';
import { formatOffsetTime } from '../../lib/job-plan-time';
import { cn } from '../../lib/utils';
import type { Job, OperatorProfile, RouteAssignment, RouteStop } from '../../types/job-plan';
import type { SolverResponse } from '../../types/job-plan-solver';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';

export interface JobPlanDetailPanelProps {
  assignments: RouteAssignment[];
  operators: OperatorProfile[];
  unassignedJobs: Job[];
  serviceDate: string;
  isViewingProposal?: boolean;
  proposedOptimization?: {
    runId: number;
    strategy: string;
    solverResult: SolverResponse;
  } | null;
  onOperatorClick?: (operatorId: number) => void;
  onJobClick?: (jobId: number) => void;
  selectedOperatorId?: number | null;
  isOpen: boolean;
  onToggle: () => void;
  className?: string;
}

function formatDurationHhMm(totalSeconds: number): string {
  const totalMinutes = Math.round(totalSeconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
}

function formatDistanceKm(meters: number): string {
  const km = meters / 1000;
  if (km < 1) {
    return `${Math.round(meters)} m`;
  }
  return `${km.toFixed(1)} km`;
}

interface OperatorComputedData {
  op: OperatorProfile;
  stops: RouteStop[];
  feasibility: SequenceFeasibility;
  missingSkillsCount: number;
}

export function JobPlanDetailPanel({
  assignments,
  operators,
  unassignedJobs,
  serviceDate,
  isViewingProposal = false,
  proposedOptimization,
  onOperatorClick,
  onJobClick,
  selectedOperatorId,
  isOpen,
  onToggle,
  className,
}: JobPlanDetailPanelProps) {
  const [expandedOperatorIds, setExpandedOperatorIds] = useState<Record<number, boolean>>({});
  const [activeTab, setActiveTab] = useState<'fleet' | 'operators' | 'unassigned'>('fleet');

  const toggleOperatorExpanded = (opId: number) => {
    setExpandedOperatorIds((prev) => ({
      ...prev,
      [opId]: !prev[opId],
    }));
  };

  // Compute sequences for all active operators
  const operatorDataList: OperatorComputedData[] = useMemo(() => {
    return operators.map((op) => {
      const assignment = assignments.find((a) => a.operatorProfileId === op.id);
      const stops = (assignment?.stops ?? []) as RouteStop[];

      const sequenceInputs = stops.map((s) => ({
        id: s.id,
        jobId: s.jobId,
        durationSeconds: s.job?.estimatedDurationSeconds ?? 3600,
        latitude: s.job?.latitude ?? s.latitudeUsed,
        longitude: s.job?.longitude ?? s.longitudeUsed,
        windowStartOffsetSeconds: s.job?.windowStartOffsetSeconds,
        windowEndOffsetSeconds: s.job?.windowEndOffsetSeconds,
        plannedArrival: s.plannedArrival,
        plannedDeparture: s.plannedDeparture,
      }));

      const feasibility = recomputeStopSequence(sequenceInputs, {
        shiftStartOffsetSeconds: op.shiftStartOffsetSeconds,
        shiftEndOffsetSeconds: op.shiftEndOffsetSeconds,
        breakDurationSeconds: op.breakDurationSeconds,
        breakWindowStartOffsetSeconds: op.breakWindowStartOffsetSeconds,
        breakWindowEndOffsetSeconds: op.breakWindowEndOffsetSeconds,
        startLocation: op.startLocation,
        endLocation: op.endLocation,
      });

      let missingSkillsCount = 0;
      for (const stop of stops) {
        if (stop.job) {
          const { eligible } = checkOperatorSkillEligibility(op, stop.job);
          if (!eligible) missingSkillsCount++;
        }
      }

      return {
        op,
        stops,
        feasibility,
        missingSkillsCount,
      };
    });
  }, [operators, assignments]);

  // Aggregate Fleet Metrics
  const fleetTotals = useMemo(() => {
    let totalServiceSec = 0;
    let totalDriveSec = 0;
    let totalDistanceM = 0;
    let totalWaitingSec = 0;
    let totalBreakSec = 0;
    let totalShiftDurationSec = 0;
    let totalAssignedStops = 0;
    let earliestStartSec: number | null = null;
    let latestFinishSec: number | null = null;
    let overShiftCount = 0;
    let windowViolationCount = 0;

    for (const data of operatorDataList) {
      const f = data.feasibility;
      totalServiceSec += f.totalServiceSeconds;
      totalDriveSec += f.totalDriveSeconds;
      totalDistanceM += f.totalDistanceMeters;
      totalWaitingSec += f.totalWaitingSeconds;
      totalBreakSec += f.totalBreakSeconds;
      totalShiftDurationSec += f.shiftDurationSeconds;
      totalAssignedStops += data.stops.length;

      if (f.isOverShift) overShiftCount++;

      for (const st of f.stops) {
        if (st.windowViolation) windowViolationCount++;
      }

      if (f.firstJobStartSeconds != null) {
        earliestStartSec =
          earliestStartSec == null
            ? f.firstJobStartSeconds
            : Math.min(earliestStartSec, f.firstJobStartSeconds);
      }
      if (f.dayFinishSeconds != null) {
        latestFinishSec =
          latestFinishSec == null
            ? f.dayFinishSeconds
            : Math.max(latestFinishSec, f.dayFinishSeconds);
      }
    }

    const totalActiveWorkSec = totalServiceSec + totalDriveSec + totalWaitingSec;
    const avgUtilizationRate =
      totalShiftDurationSec > 0
        ? Math.min(1, (totalServiceSec + totalDriveSec) / totalShiftDurationSec)
        : 0;
    const avgProductivityRate =
      totalActiveWorkSec > 0 ? Math.min(1, totalServiceSec / totalActiveWorkSec) : 0;

    return {
      totalServiceSec,
      totalDriveSec,
      totalDistanceM,
      totalWaitingSec,
      totalBreakSec,
      totalShiftDurationSec,
      totalAssignedStops,
      earliestStartSec,
      latestFinishSec,
      overShiftCount,
      windowViolationCount,
      avgUtilizationRate,
      avgProductivityRate,
    };
  }, [operatorDataList]);

  if (!isOpen) {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={onToggle}
        className={cn(
          'fixed right-4 bottom-4 z-30 shadow-lg bg-card/95 backdrop-blur-sm border-border text-foreground hover:bg-accent gap-2 h-9 px-3 rounded-full',
          className
        )}
        title="Open Route & Fleet Analytics"
      >
        <TrendingUp className="h-4 w-4 text-primary" />
        <span className="font-semibold text-xs">Route Analytics</span>
        <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4">
          {fleetTotals.totalAssignedStops} stops • {formatDistanceKm(fleetTotals.totalDistanceM)}
        </Badge>
      </Button>
    );
  }

  return (
    <aside
      className={cn(
        'w-96 flex-shrink-0 flex flex-col h-full bg-card border-l border-border/60 shadow-lg z-20 overflow-hidden font-sans transition-all duration-200',
        className
      )}
    >
      {/* ── Top Header ─────────────────────────────────────────────── */}
      <div className="p-3.5 border-b border-border/40 bg-muted/30 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <div className="h-7 w-7 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <RouteIcon className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <h3 className="font-semibold text-xs text-foreground truncate">
                {isViewingProposal ? 'Proposed Plan Analytics' : 'Route & Fleet Analytics'}
              </h3>
              {isViewingProposal && (
                <Badge
                  variant="secondary"
                  className="text-[9px] px-1 py-0 bg-primary/15 text-primary border-primary/30"
                >
                  PROPOSAL
                </Badge>
              )}
            </div>
            <p className="text-[10px] text-muted-foreground truncate">{serviceDate}</p>
          </div>
        </div>

        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggle}
            className="h-7 w-7 rounded-lg text-muted-foreground hover:text-foreground"
            title="Collapse Analytics Panel"
          >
            <PanelRightClose className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* ── Proposal Insights Callout (if viewing optimization) ─────── */}
      {isViewingProposal && proposedOptimization && (
        <div className="p-3 bg-primary/5 border-b border-primary/20 shrink-0 space-y-1.5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-primary">
              <Sparkles className="h-3.5 w-3.5" />
              <span>
                {proposedOptimization.strategy === 'fastest_finish'
                  ? 'Fastest Finish Strategy'
                  : proposedOptimization.strategy === 'minimize_distance'
                    ? 'Distance Reduction Strategy'
                    : 'Balanced Workload Strategy'}
              </span>
            </div>
            <Badge variant="outline" className="text-[9px] px-1.5 py-0 border-primary/30">
              {proposedOptimization.solverResult.routes.length} Active Routes
            </Badge>
          </div>
          <p className="text-[11px] text-muted-foreground leading-snug">
            All routes optimized for vehicle transit time, operator skill eligibility, and customer
            service windows.
          </p>
        </div>
      )}

      {/* ── Tab Switcher ───────────────────────────────────────────── */}
      <div className="px-3 pt-2.5 pb-1.5 border-b border-border/40 shrink-0">
        <div className="grid grid-cols-3 bg-muted/50 p-0.5 rounded-lg text-xs font-medium">
          <button
            type="button"
            onClick={() => setActiveTab('fleet')}
            className={cn(
              'py-1 text-center rounded-md transition-all text-xs',
              activeTab === 'fleet'
                ? 'bg-card text-foreground shadow-xs font-semibold'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            Fleet ({operators.length})
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('operators')}
            className={cn(
              'py-1 text-center rounded-md transition-all text-xs',
              activeTab === 'operators'
                ? 'bg-card text-foreground shadow-xs font-semibold'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            Operators
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('unassigned')}
            className={cn(
              'py-1 text-center rounded-md transition-all text-xs flex items-center justify-center gap-1',
              activeTab === 'unassigned'
                ? 'bg-card text-foreground shadow-xs font-semibold'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            <span>Unplaced</span>
            <Badge variant="secondary" className="text-[9px] px-1 py-0 h-3.5 font-mono">
              {unassignedJobs.length}
            </Badge>
          </button>
        </div>
      </div>

      {/* ── Scrollable Body ────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto p-3.5 space-y-4 scrollbar-thin">
        {/* ═══ TAB 1: FLEET OVERVIEW ════════════════════════════════ */}
        {activeTab === 'fleet' && (
          <div className="space-y-3.5">
            {/* Quick Metrics Grid */}
            <div className="grid grid-cols-2 gap-2">
              {/* Distance Card */}
              <div className="bg-muted/30 border border-border/50 rounded-xl p-2.5 flex flex-col gap-1">
                <div className="flex items-center gap-1.5 text-muted-foreground text-[11px]">
                  <Fuel className="h-3.5 w-3.5 text-blue-500" />
                  <span>Total Distance</span>
                </div>
                <span className="text-base font-bold tracking-tight text-foreground">
                  {formatDistanceKm(fleetTotals.totalDistanceM)}
                </span>
                <span className="text-[10px] text-muted-foreground">Across all routes</span>
              </div>

              {/* Driving Time Card */}
              <div className="bg-muted/30 border border-border/50 rounded-xl p-2.5 flex flex-col gap-1">
                <div className="flex items-center gap-1.5 text-muted-foreground text-[11px]">
                  <Navigation className="h-3.5 w-3.5 text-amber-500" />
                  <span>Drive Time</span>
                </div>
                <span className="text-base font-bold tracking-tight text-foreground">
                  {formatDurationHhMm(fleetTotals.totalDriveSec)}
                </span>
                <span className="text-[10px] text-muted-foreground">In transit on road</span>
              </div>

              {/* Service Working Time Card */}
              <div className="bg-muted/30 border border-border/50 rounded-xl p-2.5 flex flex-col gap-1">
                <div className="flex items-center gap-1.5 text-muted-foreground text-[11px]">
                  <Briefcase className="h-3.5 w-3.5 text-emerald-500" />
                  <span>On-Site Service</span>
                </div>
                <span className="text-base font-bold tracking-tight text-foreground">
                  {formatDurationHhMm(fleetTotals.totalServiceSec)}
                </span>
                <span className="text-[10px] text-muted-foreground">
                  {fleetTotals.totalAssignedStops} completed stops
                </span>
              </div>

              {/* Fleet Utilization Card */}
              <div className="bg-muted/30 border border-border/50 rounded-xl p-2.5 flex flex-col gap-1">
                <div className="flex items-center gap-1.5 text-muted-foreground text-[11px]">
                  <TrendingUp className="h-3.5 w-3.5 text-primary" />
                  <span>Fleet Utilization</span>
                </div>
                <span className="text-base font-bold tracking-tight text-foreground">
                  {Math.round(fleetTotals.avgUtilizationRate * 100)}%
                </span>
                <span className="text-[10px] text-muted-foreground">Of shift capacity</span>
              </div>
            </div>

            {/* Fleet Working Day Span */}
            <div className="bg-card border border-border/60 rounded-xl p-3 space-y-2 shadow-xs">
              <div className="flex items-center justify-between text-xs">
                <span className="font-semibold text-foreground flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5 text-primary" />
                  <span>Operational Span</span>
                </span>
                <Badge variant="outline" className="text-[10px]">
                  {fleetTotals.earliestStartSec != null && fleetTotals.latestFinishSec != null
                    ? formatDurationHhMm(fleetTotals.latestFinishSec - fleetTotals.earliestStartSec)
                    : '—'}
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs pt-1 border-t border-border/40">
                <div>
                  <span className="text-[10px] text-muted-foreground">Earliest Job Start</span>
                  <p className="font-bold text-foreground">
                    {fleetTotals.earliestStartSec != null
                      ? formatOffsetTime(fleetTotals.earliestStartSec)
                      : 'None'}
                  </p>
                </div>
                <div>
                  <span className="text-[10px] text-muted-foreground">Latest Day Finish</span>
                  <p className="font-bold text-foreground">
                    {fleetTotals.latestFinishSec != null
                      ? formatOffsetTime(fleetTotals.latestFinishSec)
                      : 'None'}
                  </p>
                </div>
              </div>
            </div>

            {/* Time Breakdown Bar */}
            <div className="bg-card border border-border/60 rounded-xl p-3 space-y-2.5 shadow-xs">
              <div className="flex items-center justify-between text-xs">
                <span className="font-semibold text-foreground">Fleet Time Allocation</span>
                <span className="text-[10px] text-muted-foreground">
                  Total {formatDurationHhMm(fleetTotals.totalShiftDurationSec)} Shift Time
                </span>
              </div>

              {/* Segmented Bar */}
              <div className="h-3 w-full rounded-full overflow-hidden bg-muted flex">
                {fleetTotals.totalShiftDurationSec > 0 && (
                  <>
                    <div
                      style={{
                        width: `${(fleetTotals.totalServiceSec / fleetTotals.totalShiftDurationSec) * 100}%`,
                      }}
                      className="bg-emerald-500 h-full transition-all"
                      title={`On-site Service: ${formatDurationHhMm(fleetTotals.totalServiceSec)}`}
                    />
                    <div
                      style={{
                        width: `${(fleetTotals.totalDriveSec / fleetTotals.totalShiftDurationSec) * 100}%`,
                      }}
                      className="bg-amber-500 h-full transition-all"
                      title={`Drive Travel: ${formatDurationHhMm(fleetTotals.totalDriveSec)}`}
                    />
                    <div
                      style={{
                        width: `${(fleetTotals.totalWaitingSec / fleetTotals.totalShiftDurationSec) * 100}%`,
                      }}
                      className="bg-yellow-500/70 h-full transition-all"
                      title={`Waiting Dead Time: ${formatDurationHhMm(fleetTotals.totalWaitingSec)}`}
                    />
                    <div
                      style={{
                        width: `${(fleetTotals.totalBreakSec / fleetTotals.totalShiftDurationSec) * 100}%`,
                      }}
                      className="bg-purple-500/60 h-full transition-all"
                      title={`Breaks: ${formatDurationHhMm(fleetTotals.totalBreakSec)}`}
                    />
                  </>
                )}
              </div>

              {/* Legend */}
              <div className="grid grid-cols-2 gap-y-1.5 text-[11px] text-muted-foreground pt-1">
                <div className="flex items-center gap-1.5">
                  <div className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
                  <span>Service: {formatDurationHhMm(fleetTotals.totalServiceSec)}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="h-2.5 w-2.5 rounded-full bg-amber-500" />
                  <span>Driving: {formatDurationHhMm(fleetTotals.totalDriveSec)}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="h-2.5 w-2.5 rounded-full bg-yellow-500/80" />
                  <span>Waiting: {formatDurationHhMm(fleetTotals.totalWaitingSec)}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="h-2.5 w-2.5 rounded-full bg-purple-500/60" />
                  <span>Breaks: {formatDurationHhMm(fleetTotals.totalBreakSec)}</span>
                </div>
              </div>
            </div>

            {/* Warnings Alert Box if any violations */}
            {(fleetTotals.overShiftCount > 0 || fleetTotals.windowViolationCount > 0) && (
              <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-900 dark:text-amber-300 text-xs space-y-1">
                <div className="flex items-center gap-1.5 font-semibold">
                  <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" />
                  <span>Route Attention Flags</span>
                </div>
                {fleetTotals.overShiftCount > 0 && (
                  <p className="text-[11px]">
                    • {fleetTotals.overShiftCount} operator(s) planned to exceed scheduled shift
                    hours.
                  </p>
                )}
                {fleetTotals.windowViolationCount > 0 && (
                  <p className="text-[11px]">
                    • {fleetTotals.windowViolationCount} stop(s) arrive outside customer requested
                    service windows.
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* ═══ TAB 2: OPERATOR ROUTES BREAKDOWN ══════════════════════ */}
        {activeTab === 'operators' && (
          <div className="space-y-3">
            {operatorDataList.map((data) => {
              const { op, stops, feasibility, missingSkillsCount } = data;
              const isExpanded = !!expandedOperatorIds[op.id];
              const isSelected = selectedOperatorId === op.id;

              return (
                <div
                  key={op.id}
                  className={cn(
                    'bg-card rounded-xl border transition-all shadow-xs overflow-hidden',
                    isSelected
                      ? 'border-primary ring-1 ring-primary/40'
                      : 'border-border/60 hover:border-border'
                  )}
                >
                  {/* Operator Header Card */}
                  <div
                    onClick={() => {
                      toggleOperatorExpanded(op.id);
                      if (onOperatorClick) onOperatorClick(op.id);
                    }}
                    className="p-3 cursor-pointer hover:bg-muted/30 flex flex-col gap-2 select-none"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="h-6 w-6 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-xs shrink-0">
                          {(op.user?.fullName || op.user?.email || 'Op')[0].toUpperCase()}
                        </div>
                        <span className="font-semibold text-xs text-foreground truncate">
                          {op.user?.fullName || op.user?.email || `Operator #${op.id}`}
                        </span>
                      </div>

                      <div className="flex items-center gap-1.5 shrink-0">
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                          {stops.length} stop{stops.length !== 1 ? 's' : ''}
                        </Badge>
                        {feasibility.isOverShift && (
                          <Badge
                            variant="destructive"
                            className="text-[9px] px-1 py-0 bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30"
                          >
                            +{Math.round(feasibility.overrunSeconds / 60)}m Over
                          </Badge>
                        )}
                        {missingSkillsCount > 0 && (
                          <Badge
                            variant="outline"
                            className="text-[9px] px-1 py-0 bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30"
                          >
                            {missingSkillsCount} skill flag{missingSkillsCount !== 1 ? 's' : ''}
                          </Badge>
                        )}
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        )}
                      </div>
                    </div>

                    {/* Quick Stats Row */}
                    <div className="grid grid-cols-3 gap-1 text-[11px] text-muted-foreground pt-1 border-t border-border/30">
                      <div>
                        <span className="text-[9px] uppercase tracking-wider text-muted-foreground/70">
                          Drive
                        </span>
                        <p className="font-medium text-foreground">
                          {formatDurationHhMm(feasibility.totalDriveSeconds)}
                        </p>
                      </div>
                      <div>
                        <span className="text-[9px] uppercase tracking-wider text-muted-foreground/70">
                          Distance
                        </span>
                        <p className="font-medium text-foreground">
                          {formatDistanceKm(feasibility.totalDistanceMeters)}
                        </p>
                      </div>
                      <div>
                        <span className="text-[9px] uppercase tracking-wider text-muted-foreground/70">
                          Utilization
                        </span>
                        <p className="font-medium text-foreground">
                          {Math.round(feasibility.utilizationRate * 100)}%
                        </p>
                      </div>
                    </div>

                    {/* Mini Day Timeline */}
                    <div className="flex items-center justify-between text-[10px] text-muted-foreground pt-0.5">
                      <span>
                        Shift: {formatOffsetTime(op.shiftStartOffsetSeconds)} –{' '}
                        {formatOffsetTime(op.shiftEndOffsetSeconds)}
                      </span>
                      {feasibility.dayFinishSeconds != null && (
                        <span className="font-medium text-foreground">
                          Finishes ~{formatOffsetTime(feasibility.dayFinishSeconds)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Expanded Stops Itinerary */}
                  {isExpanded && (
                    <div className="p-3 bg-muted/20 border-t border-border/40 space-y-2 text-xs">
                      <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                        Route Sequence & Timing
                      </div>

                      {stops.length === 0 ? (
                        <p className="text-xs text-muted-foreground italic py-2 text-center">
                          No stops assigned to this operator.
                        </p>
                      ) : (
                        <div className="space-y-2 relative before:absolute before:left-2 before:top-2 before:bottom-2 before:w-0.5 before:bg-border/60 pl-5">
                          {feasibility.stops.map((st, idx) => {
                            const stop = stops.find((s) => s.jobId === st.jobId);
                            const job = stop?.job;
                            if (!job) return null;

                            return (
                              <div
                                key={st.jobId}
                                onClick={() => onJobClick?.(job.id)}
                                className={cn(
                                  'relative p-2 rounded-lg border bg-card/80 hover:bg-card transition-all cursor-pointer space-y-1',
                                  st.windowViolation
                                    ? 'border-red-500/40 bg-red-500/5'
                                    : 'border-border/50 hover:border-border'
                                )}
                              >
                                {/* Dot on timeline */}
                                <div className="absolute -left-[17px] top-3 h-2.5 w-2.5 rounded-full bg-primary border-2 border-card" />

                                <div className="flex items-center justify-between">
                                  <span className="font-semibold text-xs text-foreground truncate">
                                    #{idx + 1} {job.title}
                                  </span>
                                  <span className="text-[10px] font-mono text-muted-foreground shrink-0">
                                    {formatOffsetTime(st.serviceStartSeconds)} –{' '}
                                    {formatOffsetTime(st.plannedDepartureSeconds)}
                                  </span>
                                </div>

                                <p className="text-[11px] text-muted-foreground truncate">
                                  {job.formattedAddress || 'No address specified'}
                                </p>

                                {/* Travel indicator */}
                                {st.driveSecondsFromPrevious > 0 && (
                                  <div className="flex items-center gap-1.5 text-[10px] text-amber-600 dark:text-amber-400">
                                    <Navigation className="h-2.5 w-2.5 rotate-45" />
                                    <span>
                                      {Math.round(st.driveSecondsFromPrevious / 60)}m drive (
                                      {formatDistanceKm(st.driveDistanceMetersFromPrevious)})
                                    </span>
                                  </div>
                                )}

                                {st.waitingSeconds > 0 && (
                                  <div className="flex items-center gap-1.5 text-[10px] text-yellow-600 dark:text-yellow-400">
                                    <Clock className="h-2.5 w-2.5" />
                                    <span>
                                      {Math.round(st.waitingSeconds / 60)}m early wait for window
                                    </span>
                                  </div>
                                )}

                                {st.windowViolation && (
                                  <div className="flex items-center gap-1 text-[10px] text-red-600 dark:text-red-400 font-medium">
                                    <AlertTriangle className="h-3 w-3" />
                                    <span>{st.windowViolationReason}</span>
                                  </div>
                                )}
                              </div>
                            );
                          })}

                          {/* Return to Depot Trip */}
                          {feasibility.returnDriveSeconds > 0 && (
                            <div className="relative p-2 rounded-lg border border-dashed border-border/60 bg-muted/20 text-[11px] text-muted-foreground flex items-center justify-between">
                              <div className="absolute -left-[17px] top-3 h-2 w-2 rounded-full bg-muted-foreground/60 border border-card" />
                              <div className="flex items-center gap-1.5">
                                <Truck className="h-3.5 w-3.5 text-muted-foreground" />
                                <span>Return transit to end location</span>
                              </div>
                              <span className="font-mono text-[10px]">
                                +{Math.round(feasibility.returnDriveSeconds / 60)}m (
                                {formatDistanceKm(feasibility.returnDriveDistanceMeters)})
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ═══ TAB 3: UNASSIGNED / UNPLACED JOBS ════════════════════ */}
        {activeTab === 'unassigned' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between text-xs">
              <span className="font-semibold text-foreground">
                Unplaced Jobs Pool ({unassignedJobs.length})
              </span>
              <span className="text-[10px] text-muted-foreground">
                Estimated duration:{' '}
                {formatDurationHhMm(
                  unassignedJobs.reduce((acc, j) => acc + (j.estimatedDurationSeconds || 3600), 0)
                )}
              </span>
            </div>

            {unassignedJobs.length === 0 ? (
              <div className="py-8 flex flex-col items-center justify-center text-center text-muted-foreground space-y-1">
                <CheckCircle2 className="h-6 w-6 text-emerald-500" />
                <p className="text-xs font-medium text-foreground">All jobs allocated</p>
                <p className="text-[11px]">No unassigned jobs remaining for this date.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {unassignedJobs.map((job) => (
                  <div
                    key={job.id}
                    onClick={() => onJobClick?.(job.id)}
                    className="p-2.5 rounded-xl border border-border/60 bg-card hover:border-primary/50 transition-all cursor-pointer space-y-1"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-xs text-foreground truncate">
                        {job.title}
                      </span>
                      <Badge variant="secondary" className="text-[9px] px-1.5 py-0 font-mono">
                        {Math.round(job.estimatedDurationSeconds / 60)}m
                      </Badge>
                    </div>

                    <p className="text-[11px] text-muted-foreground truncate">
                      {job.formattedAddress || 'No address specified'}
                    </p>

                    {job.windowStartOffsetSeconds != null && job.windowEndOffsetSeconds != null && (
                      <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                        <Clock className="h-2.5 w-2.5" />
                        <span>
                          Window: {formatOffsetTime(job.windowStartOffsetSeconds)} –{' '}
                          {formatOffsetTime(job.windowEndOffsetSeconds)}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Footer Status Summary ─────────────────────────────────── */}
      <div className="p-3 border-t border-border/40 bg-muted/20 text-[11px] text-muted-foreground flex items-center justify-between shrink-0">
        <span className="truncate">
          {operators.length} Operator{operators.length !== 1 ? 's' : ''} •{' '}
          {fleetTotals.totalAssignedStops} Stop{fleetTotals.totalAssignedStops !== 1 ? 's' : ''}
        </span>
        <span className="font-semibold text-foreground">
          {Math.round(fleetTotals.avgUtilizationRate * 100)}% Utilized
        </span>
      </div>
    </aside>
  );
}
